import type { OptionType } from "components/inputs";
import debounce from "lodash/debounce";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useGetClientsQuery } from "services/client-api";
import { useGetUsersQuery } from "services/users-api";

import type { TasksFilters } from "../filters/TasksReportFilters";

type ExtendedOptionType = OptionType & {
  clientId?: string;
};

export const useTasksForm = () => {
  const [clientOptions, setClientOptions] = useState<ExtendedOptionType[]>([]);
  const [clientSearchTerm, setClientSearchTerm] = useState<string>("");
  const [assigneeOptions, setAssigneeOptions] = useState<OptionType[]>([]);
  const [assigneeSearchTerm, setAssigneeSearchTerm] = useState<string>("");
  const [formKey, setFormKey] = useState(0);

  const form = useForm<TasksFilters>({
    defaultValues: {
      client_id: null,
      report_type: null,
      assignee_ids: [],
      status: null,
      service_type: null,
      created_after: null,
      created_before: null,
      started_after: null,
      started_before: null,
      completed_after: null,
      completed_before: null,
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = form;

  // debounced search functions
  const debouncedSetClientSearchTerm = useMemo(
    () =>
      debounce((value: string) => {
        setClientSearchTerm(value);
      }, 500),
    [],
  );

  const debouncedSetAssigneeSearchTerm = useMemo(
    () =>
      debounce((value: string) => {
        setAssigneeSearchTerm(value);
      }, 500),
    [],
  );

  // cleanup debounced functions on unmount
  useEffect(() => {
    return () => {
      debouncedSetClientSearchTerm.cancel();
      debouncedSetAssigneeSearchTerm.cancel();
    };
  }, [debouncedSetClientSearchTerm, debouncedSetAssigneeSearchTerm]);

  // API queries - Fixed: useGetUsersQuery doesn't take searchTerm, so we'll filter client-side
  const { data: clients, isFetching: isClientsFetching } = useGetClientsQuery(
    { searchTerm: clientSearchTerm },
    { skip: !clientSearchTerm },
  );

  // Get all users and filter client-side since useGetUsersQuery doesn't support search
  const { data: users, isFetching: isAssigneesFetching } = useGetUsersQuery({
    userType: "ORGANIZATION",
    page: 1,
    pageSize: 500,
  });

  // update client options
  useEffect(() => {
    if (!clientSearchTerm) {
      setClientOptions([]);
    } else if (clients) {
      const options =
        clients.results?.map((client) => ({
          label: client.name,
          value: client.id.toString(),
        })) ?? [];
      setClientOptions(options);
    }
  }, [clients, clientSearchTerm]);

  // update assignee options - filter client-side
  useEffect(() => {
    if (!users) {
      setAssigneeOptions([]);
      return;
    }

    let filteredUsers = users.results ?? [];

    // Filter by search term if provided
    if (assigneeSearchTerm) {
      filteredUsers = filteredUsers.filter((user) =>
        `${user.first_name} ${user.last_name}`
          .toLowerCase()
          .includes(assigneeSearchTerm.toLowerCase()),
      );
    }

    const options = filteredUsers.map((user) => ({
      label: `${user.first_name} ${user.last_name}`.trim(),
      value: user.id.toString(),
    }));

    setAssigneeOptions(options);
  }, [users, assigneeSearchTerm]);

  // Status options
  const statusOptions = useMemo(
    () => [
      { label: "Pending", value: "P" },
      { label: "In Progress", value: "I" },
      { label: "Completed", value: "C" },
    ],
    [],
  );

  // Clear filters handler
  const handleClearFilters = () => {
    const currentReportType = watch("report_type");

    reset({
      client_id: null,
      report_type: currentReportType,
      assignee_ids: [],
      status: null,
      service_type: null,
      created_after: null,
      created_before: null,
      started_after: null,
      started_before: null,
      completed_after: null,
      completed_before: null,
    });
    setClientSearchTerm("");
    setAssigneeSearchTerm("");
    setFormKey((prev) => prev + 1);
  };

  return {
    form: {
      control,
      handleSubmit,
      reset,
      watch,
      errors,
      isSubmitting,
    },
    options: {
      clientOptions,
      assigneeOptions,
      statusOptions,
    },
    loading: {
      isClientsFetching,
      isAssigneesFetching,
    },
    search: {
      debouncedSetClientSearchTerm,
      debouncedSetAssigneeSearchTerm,
    },
    formKey,
    handleClearFilters,
  };
};
